import { useState, useRef, useEffect } from 'react';
import { Dialog, Typography, Stack, Link, Tooltip, IconButton, TextField, Button } from "@mui/material";
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import CircularProgress from '@mui/material/CircularProgress'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import { deleteCoords, updateItemFields, markUnfit, deleteComment, updateComment } from '../supabaseApi'
import { t } from '../utils/strings'
import { accentText, accent } from '../utils/colorDict';

const COLLECTION = process.env.REACT_APP_COLLECTION || null;
const canEdit = (authData: any) => authData?.user?.role === 'admin' || authData?.user?.role === 'editor';
const isAdmin = (authData: any) => authData?.user?.role === 'admin';

const isRtl = (text: string) => {
  if (!text) return false;
  const first = text.match(/[\p{L}\p{N}]/u);
  if (!first) return false;
  return /[\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Syriac}\p{Script=Thaana}]/u.test(first[0]);
};

// Chrome and Edge handle RTL bracket mirroring differently with dir="auto":
//   Chrome: mirrors brackets on DISPLAY elements, but NOT in textarea during typing
//   Edge:   mirrors brackets in TEXTAREA during typing, but NOT on display elements
// We detect Edge via userAgent and compensate accordingly.
const BRACKET_SWAP: Record<string, string> = { '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{' };
const swapBrackets = (text: string): string => {
  if (!text || !isRtl(text)) return text;
  return text.replace(/[()[\]{}]/g, (ch: string) => BRACKET_SWAP[ch] || ch);
};
const isEdgeBrowser = typeof navigator !== 'undefined' && /Edg\//.test(navigator.userAgent);

// INPUT: Chrome textarea doesn't mirror brackets when direction is inherited/auto.
//        But when direction: rtl is explicitly set via style, Chrome DOES mirror natively.
//        So only swap when content is RTL but the textarea lacks explicit RTL direction.
const swapBracketForInput = (e: any, currentText: string, setText: (v: string) => void) => {
  if (isEdgeBrowser) return; // Edge mirrors in textarea natively
  if (!isRtl(currentText) || !BRACKET_SWAP[e.key]) return;
  // If the textarea has explicit direction: rtl set, Chrome mirrors natively — skip swap
  const computedDir = e.target?.style?.direction;
  if (computedDir === 'rtl') return;
  e.preventDefault();
  const ta = e.target;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  setText(currentText.substring(0, start) + BRACKET_SWAP[e.key] + currentText.substring(end));
  setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + 1; }, 0);
};

// STORAGE: With explicit direction:rtl on the textarea, both Chrome and Edge
// mirror brackets natively. DB always stores canonical bracket characters.
const prepareForStorage = (text: string): string => {
  return text;
};

// DISPLAY: DB stores canonical chars.
//   Chrome: dir="auto" does NOT mirror brackets on display elements → swap explicitly.
//   Edge:   dir="auto" DOES mirror brackets on display elements → don't swap (would double-flip).
const prepareForDisplay = (text: string): string => {
  if (isEdgeBrowser) return text;
  return swapBrackets(text);
};

const ModalImage = ({ imageUrl, fallbackUrl, onSize }: { imageUrl: string, fallbackUrl?: string, onSize?: (w: number, h: number) => void }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  // Scholten: primary is Leiden, fallback is NAS. Swap once on first error.
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setError(false);
    setUsingFallback(false);
  }, [imageUrl]);

  const handleLoad = (e: any) => {
    setLoaded(true);
    onSize?.(e.target.naturalWidth, e.target.naturalHeight);
  };

  const handleError = () => {
    if (!usingFallback && fallbackUrl) { setUsingFallback(true); return; }
    setError(true);
  };

  const src = usingFallback && fallbackUrl ? fallbackUrl : imageUrl;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e0e0e0' }}>
      {!loaded && !error && <CircularProgress size={30} sx={{ position: 'absolute', color: '#999' }} />}
      {error && <BrokenImageIcon sx={{ position: 'absolute', color: '#ccc', fontSize: 60 }} />}
      {src && !error && (
        <img
          src={src}
          alt=""
          onLoad={handleLoad}
          onError={handleError}
          style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', objectFit: 'contain', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
        />
      )}
    </div>
  );
};

const CommentsPanel = ({ authData, itemComments, handleComment, handleComments, itemPk, lang, isPhone }: any) => {
  const [commentInput, setCommentInput] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')

  const handleDelete = async (commentId: number) => {
    try {
      const updated = await deleteComment(commentId, itemPk)
      handleComments(updated)
    } catch (err) {
      console.error('Delete comment error:', err)
    }
  }

  const handleEdit = (comment: any) => {
    setEditingId(comment.id)
    setEditText(comment.text)
  }

  const handleSaveEdit = async () => {
    if (!editText.trim()) return
    try {
      const updated = await updateComment(editingId, prepareForStorage(editText.trim()), itemPk)
      handleComments(updated)
      setEditingId(null)
    } catch (err) {
      console.error('Edit comment error:', err)
    }
  }

  const handleSubmit = () => {
    if (!commentInput.trim()) return
    handleComment({ author: authData.user.pk, text: prepareForStorage(commentInput), item: itemPk })
    setCommentInput('')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: isPhone ? '4px 8px' : '10px', boxSizing: 'border-box', overflowY: 'auto', direction: lang === 'he' ? 'rtl' : 'ltr', textAlign: lang === 'he' ? 'right' : 'left' }}>
      {/* Comments list */}
      {itemComments?.length ? itemComments.map((comment: any) => (
        <Stack key={comment.id} sx={{ padding: isPhone ? '2px 0' : '6px 0', borderBottom: '1px solid #e0e0e0', mb: isPhone ? 0 : 0.5, flexShrink: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{comment.author.username}</Typography>
              <Typography fontSize={10} variant='subtitle2' color="text.secondary">{comment.date ? new Date(comment.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</Typography>
            </span>
            {(authData?.user?.id === comment.author_id || authData?.user?.role === 'admin') && editingId !== comment.id && (
              <span>
                <IconButton size="small" onClick={() => handleEdit(comment)} sx={{ color: '#999', '&:hover': { color: accent } }}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(comment.id)} sx={{ color: '#999', '&:hover': { color: '#d32f2f' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            )}
          </span>
          {editingId === comment.id ? (
            <span>
              <TextField fullWidth size="small" multiline rows={2} value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => swapBracketForInput(e, editText, setEditText)}
                inputProps={{ style: { padding: '4px', direction: editText ? (isRtl(editText) ? 'rtl' : 'ltr') : (lang === 'he' ? 'rtl' : 'ltr'), textAlign: editText ? (isRtl(editText) ? 'right' : 'left') : (lang === 'he' ? 'right' : 'left') } }} />
              <span>
                <IconButton size="small" onClick={handleSaveEdit} sx={{ color: accent }}><CheckIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => setEditingId(null)} sx={{ color: '#999' }}><CloseIcon fontSize="small" /></IconButton>
              </span>
            </span>
          ) : (
            <Typography variant="body2" dir="auto" sx={{ py: isPhone ? 0 : 0.5, direction: isRtl(comment.text) ? 'rtl' : 'ltr', textAlign: isRtl(comment.text) ? 'right' : 'left' }}>
              {comment.text}
            </Typography>
          )}
        </Stack>
      )) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: isPhone ? 0.25 : 1, flexShrink: 0 }}>{t('noCommentsYet', lang)}</Typography>
      )}

      {/* Comment input - right after comments */}
      {(authData?.user?.role === 'admin' || authData?.user?.role === 'editor') ? (
        <div style={{ flexShrink: 0, paddingTop: isPhone ? 4 : 8, borderTop: '1px solid #e0e0e0', marginTop: isPhone ? 4 : 8 }}>
          <TextField size="small" fullWidth multiline rows={isPhone ? 1 : 2} placeholder={t('yourComment', lang)}
            value={commentInput}
            onChange={(e) => setCommentInput(e.target.value)}
            onKeyDown={(e) => {
              swapBracketForInput(e, commentInput, setCommentInput);
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            }}
            inputProps={{ style: { padding: isPhone ? '4px' : '6px', fontSize: 13, direction: commentInput ? (isRtl(commentInput) ? 'rtl' : 'ltr') : (lang === 'he' ? 'rtl' : 'ltr'), textAlign: commentInput ? (isRtl(commentInput) ? 'right' : 'left') : (lang === 'he' ? 'right' : 'left') } }}
          />
          <Button size="small" onClick={handleSubmit} sx={{ mt: 0, py: isPhone ? 0 : 0.5, minHeight: isPhone ? 24 : undefined, textTransform: 'none' }}>{t('addComment', lang)}</Button>
        </div>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: isPhone ? 0.25 : 1 }}>{t('logInToComment', lang)}</Typography>
      )}
    </div>
  )
}

const ItemModal = ({ handleModalData, photoModalData, authData, toggleQuestionable, setItemsFromApi, handleItemToPosition, setLastRemovedLocationId, onNavigate, itemComments, handleComment, handleComments, itemPk, lang }: any) => {
  const [, forceUpdate] = useState(0)
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingDesc, setEditingDesc] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [descDraft, setDescDraft] = useState('')
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  )
  // On phone, the metadata overlay starts collapsed so the image is unobstructed.
  const [overlayCollapsed, setOverlayCollapsed] = useState<boolean>(isPhone)
  const [modalWidth, setModalWidth] = useState('60vw')
  const [imageAspect, setImageAspect] = useState<number | undefined>(undefined)
  const zoomRef = useRef<any>(null)
  // Track touch-swipe start for phone navigation between modal items.
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 600px)')
    const onChange = () => setIsPhone(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])



  // Read questionable_by directly from photoModalData (mutated in place on toggle)
  const questionableBy = photoModalData?.properties?.questionable_by || []

  const hasLocation = photoModalData?.geometry?.coordinates?.length > 0

  const handleCancelLocation = async () => {
    if (!window.confirm(t('removeGeotagConfirm', lang))) return
    try {
      await deleteCoords(photoModalData.properties.id)
      setItemsFromApi(prev => prev.filter(item => item.properties?.id !== photoModalData.properties.id))
      setLastRemovedLocationId(photoModalData.properties.id)
      handleModalData(null)
    } catch (err) {
      alert(t('removeLocationError', lang, { error: err?.message || 'Unknown error' }))
    }
  }

  const handleEditLocation = () => {
    const item = photoModalData
    handleModalData(null)
    handleItemToPosition(item)
  }

  const handleToggleUnfit = async () => {
    const currentVal = photoModalData.properties.unfit_for_tagging || false
    try {
      await markUnfit(photoModalData.properties.id, !currentVal)
      photoModalData.properties.unfit_for_tagging = !currentVal
      forceUpdate(n => n + 1)
      setItemsFromApi(prev => prev.map(item => {
        if (item.properties?.id === photoModalData.properties.id) {
          item.properties.unfit_for_tagging = !currentVal
        }
        return item
      }))
    } catch (err) {
      alert(t('saveError', lang, { error: err?.message || 'Unknown error' }))
    }
  }

  const handleToggleQuestionable = async () => {
    if (!authData?.user) return
    const result = await toggleQuestionable(photoModalData.properties.id, authData.user.id)
    photoModalData.properties.questionable_by = result
    photoModalData.properties.questionable_by_str = JSON.stringify(result)
    forceUpdate(n => n + 1)
    setItemsFromApi(prev => prev.map(item => {
      if (item.properties?.id === photoModalData.properties.id) {
        item.properties.questionable_by = result
        item.properties.questionable_by_str = JSON.stringify(result)
      }
      return item
    }))
  }

  const startEditTitle = () => {
    const useHeb = lang === 'he' && photoModalData.properties.title_heb;
    setTitleDraft((useHeb ? photoModalData.properties.title_heb : photoModalData.properties.title) || '')
    setEditingTitle(true)
  }

  const saveTitle = async () => {
    try {
      const field = (lang === 'he' && photoModalData.properties.title_heb) ? 'title_heb' : 'title';
      await updateItemFields(photoModalData.properties.id, { [field]: titleDraft })
      photoModalData.properties[field] = titleDraft
      setEditingTitle(false)
      forceUpdate(n => n + 1)
    } catch (err) {
      alert(t('failedToSaveTitle', lang, { error: err?.message || 'Unknown error' }))
    }
  }

  const startEditDesc = () => {
    const useHeb = lang === 'he' && photoModalData.properties.desc_heb;
    setDescDraft((useHeb ? photoModalData.properties.desc_heb : photoModalData.properties.desc) || '')
    setEditingDesc(true)
  }

  const saveDesc = async () => {
    try {
      const field = (lang === 'he' && photoModalData.properties.desc_heb) ? 'desc_heb' : 'desc';
      await updateItemFields(photoModalData.properties.id, { [field]: descDraft })
      photoModalData.properties[field] = descDraft
      setEditingDesc(false)
      forceUpdate(n => n + 1)
    } catch (err) {
      alert(t('failedToSaveDescription', lang, { error: err?.message || 'Unknown error' }))
    }
  }

  const formatYear = (props: any) => {
    if (!props.year) return null
    if (props.year_end && props.year_end !== props.year) return `${props.year}–${props.year_end}`
    return String(props.year)
  }

  const handleImageSize = (w: number, h: number) => {
    // Phone: image fills the column at its native aspect ratio (capped by maxHeight),
    // no sidebar budget (comments stack below).
    if (isPhone) {
      setModalWidth('95vw');
      setImageAspect(w / h);
      return;
    }
    // Desktop: aspect-ratio width within 85vh, leaving room for the 300px sidebar.
    const SIDEBAR_W = 312;
    const maxH = window.innerHeight * 0.85;
    const maxW = window.innerWidth * 0.95 - SIDEBAR_W;
    const aspectW = (w / h) * maxH;
    setModalWidth(Math.min(aspectW, maxW) + 'px');
  }

  // Up/Down arrow keys for zoom
  useEffect(() => {
    if (!photoModalData) return
    const handleZoomKeys = (e: KeyboardEvent) => {
      if (!zoomRef.current) return
      if (e.key === 'ArrowUp') { e.preventDefault(); zoomRef.current.zoomIn(0.5) }
      if (e.key === 'ArrowDown') { e.preventDefault(); zoomRef.current.zoomOut(0.5) }
    }
    document.addEventListener('keydown', handleZoomKeys)
    return () => document.removeEventListener('keydown', handleZoomKeys)
  }, [photoModalData])

  // Reset zoom when navigating to a different item (via left/right arrows)
  useEffect(() => {
    if (zoomRef.current?.resetTransform) {
      zoomRef.current.resetTransform()
    }
  }, [photoModalData?.properties?.id])

  const isAleksandrowicz = COLLECTION === 'aleksandrowicz' ||
    photoModalData?.properties?.collection === 'aleksandrowicz'
  const isMatson = COLLECTION === 'matson' ||
    photoModalData?.properties?.collection === 'matson'
  const isBenShahar = COLLECTION === 'ben_shahar' ||
    photoModalData?.properties?.collection === 'ben_shahar'
  const isVanDePoll = COLLECTION === 'van_de_poll' ||
    photoModalData?.properties?.collection === 'van_de_poll'
  const isTaycm = COLLECTION === 'taycm' ||
    photoModalData?.properties?.collection === 'taycm'

  // Matson LoC pk: stored either as additional_id (preferred) or derivable from
  // external_id "Matson Collection-Library of Congress/{pk}_{suffix}".
  const matsonPk = isMatson
    ? (photoModalData?.properties?.additional_id
        || (photoModalData?.properties?.external_id
              ? String(photoModalData.properties.external_id).split('/').pop()?.split('_')[0]
              : null))
    : null

  return <>
    {photoModalData ? (
      <Dialog open={photoModalData ? true : false}
        maxWidth={false}
        disableEnforceFocus={true}
        onClose={() => { handleModalData(null); setEditingTitle(false); setEditingDesc(false); setModalWidth('60vw'); }}
        PaperProps={{ style: { maxWidth: '95vw', maxHeight: '95vh', display: 'flex', flexDirection: isPhone ? 'column' : 'row', alignItems: 'stretch', gap: 4, padding: 4, backgroundColor: 'white', position: 'relative', overflow: isPhone ? 'auto' : 'visible', margin: isPhone ? 8 : undefined } }}
      >
        {/* Main content: image with overlaid toolbar + info.
            Phone: width 100%, height shrinks to image aspect (capped at 65vh so comments stay visible).
            Desktop: aspect-width fits inside 85vh. */}
        <div
          style={{ position: 'relative', overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 4, width: isPhone ? '100%' : modalWidth, height: isPhone ? 'auto' : '85vh', maxHeight: isPhone ? '65vh' : '85vh', aspectRatio: isPhone ? imageAspect : undefined, flexShrink: 0, transition: 'width 0.3s ease' }}
          onTouchStart={isPhone && onNavigate ? (e) => {
            if (e.touches.length !== 1) { touchStartRef.current = null; return; }
            // Only track when zoom is at 1; otherwise let the zoom library pan.
            const scale = zoomRef.current?.instance?.transformState?.scale ?? 1;
            if (scale > 1.05) { touchStartRef.current = null; return; }
            const t = e.touches[0];
            touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
          } : undefined}
          onTouchEnd={isPhone && onNavigate ? (e) => {
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (!start) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - start.x;
            const dy = t.clientY - start.y;
            const dt = Date.now() - start.t;
            // Horizontal swipe past 60px, mostly horizontal, under 600ms.
            if (Math.abs(dx) < 60) return;
            if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
            if (dt > 600) return;
            // Swipe left = next (+1), swipe right = previous (-1). Mirror in RTL.
            const rtl = lang === 'he';
            const dir = dx < 0 ? (rtl ? -1 : 1) : (rtl ? 1 : -1);
            onNavigate(dir);
          } : undefined}
        >
          {/* Image fills full height */}
          <div style={{ width: '100%', height: '100%' }}>
          <TransformWrapper ref={zoomRef}>
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ModalImage
              imageUrl={photoModalData.properties.modal_url}
              fallbackUrl={photoModalData.properties.modal_fallback}
              onSize={handleImageSize}/>
            </TransformComponent>
          </TransformWrapper>
          </div>

          {/* Overlaid toolbar + info at bottom (stacked when image fails) */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, pointerEvents: 'auto' }}>
            <Stack direction="row" alignItems="center" sx={{
                          backgroundColor: isAleksandrowicz ? 'rgba(27, 75, 103, 0.5)' : isTaycm ? 'rgba(44, 38, 122, 0.5)' : 'rgba(0, 127, 173, 0.5)',
                          padding: isPhone ? '0 6px' : '2px 8px',
                          flexShrink: 0,
                          direction: lang === 'he' ? 'rtl' : 'ltr',
                          // Tighter buttons on phone so the toolbar doesn't waste vertical space.
                          ...(isPhone && {
                            '& .MuiIconButton-root': { padding: '2px' },
                            '& .MuiSvgIcon-root': { fontSize: 18 },
                          }) }}>
              {canEdit(authData) && hasLocation && <>
                <Tooltip title={t('removeLocation', lang)}>
                  <IconButton size="small" onClick={handleCancelLocation}>
                    <LocationOffIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('editLocation', lang)}>
                  <IconButton size="small" onClick={handleEditLocation}>
                    <EditLocationIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                  </IconButton>
                </Tooltip>
              </>}
              {canEdit(authData) && !hasLocation && (
                <Tooltip title={t('addLocation', lang)}>
                  <IconButton size="small" onClick={handleEditLocation}>
                    <AddLocationAltIcon sx={{ color: '#90ee90' }} />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit(authData) ?
                <Tooltip title={questionableBy?.includes(authData.user.id) ? t('removeQuestionableFlag', lang) : t('flagAsQuestionable', lang)}>
                  <IconButton size="small" onClick={handleToggleQuestionable}>
                    <NotListedLocationIcon sx={{ color: questionableBy?.includes(authData.user.id) ? '#ff9800' : 'rgba(255,255,255,0.6)' }} />
                  </IconButton>
                </Tooltip>
              : questionableBy?.length > 0 &&
                <NotListedLocationIcon sx={{ color: '#ff9800' }} />
              }
              {questionableBy?.length > 0 &&
                <Typography fontSize={isPhone ? 10 : 12} sx={{ color: 'white', mx: 0.5 }}>
                  {t('questionableLocation', lang)}
                </Typography>
              }
              {!hasLocation && canEdit(authData) &&
                <Tooltip title={photoModalData.properties.unfit_for_tagging ? t('markAsTaggable', lang) : t('markAsUnfit', lang)}>
                  <IconButton size="small" onClick={handleToggleUnfit}>
                    <ImageNotSupportedIcon sx={{ color: photoModalData.properties.unfit_for_tagging ? '#ff9800' : 'rgba(255,255,255,0.6)' }} />
                  </IconButton>
                </Tooltip>
              }
              {photoModalData.properties.unfit_for_tagging &&
                <Typography fontSize={isPhone ? 10 : 12} sx={{ color: 'white', mx: 0.5 }}>
                  {t('unfitForTagging', lang)}
                </Typography>
              }
              <div style={{ flex: 1 }} />
              <IconButton size="small" onClick={() => setOverlayCollapsed(c => !c)}>
                {overlayCollapsed ? <KeyboardArrowUpIcon sx={{ color: 'rgba(255,255,255,0.8)' }} /> : <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
              </IconButton>
            </Stack>
            <div style={{ maxHeight: overlayCollapsed ? 0 : 300, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
            <Stack padding={isPhone ? '4px 10px' : '8px 16px'} spacing={isPhone ? 0.1 : 0.5} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.65)', flexShrink: 0, width: 0, minWidth: '100%', boxSizing: 'border-box', '& *': { overflowWrap: 'break-word', wordBreak: 'break-word' }, ...(isPhone && { '& .MuiTypography-caption': { fontSize: 10, lineHeight: 1.2 } }) }} style={{ direction: lang === 'he' ? 'rtl' : 'ltr', textAlign: lang === 'he' ? 'right' : 'left' }}>
              {/* Title - editable */}
              {editingTitle ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <TextField size="small" fullWidth value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                    autoFocus variant="standard" sx={{ '& .MuiInput-input': { color: 'white' }, '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.4)' } }} />
                  <IconButton size="small" onClick={saveTitle}><CheckIcon fontSize="small" sx={{ color: '#66ff66' }} /></IconButton>
                  <IconButton size="small" onClick={() => setEditingTitle(false)}><CloseIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.8)' }} /></IconButton>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Typography variant={isPhone ? 'body2' : 'body1'} sx={{ color: 'white', ...(isPhone && { fontSize: 12, lineHeight: 1.25, fontWeight: 500 }) }}>{(lang === 'he' && photoModalData.properties.title_heb) ? photoModalData.properties.title_heb : photoModalData.properties.title}</Typography>
                  {isAdmin(authData) && <IconButton size="small" onClick={startEditTitle}><EditIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} /></IconButton>}
                </div>
              )}

              {/* Description - editable */}
              {(photoModalData.properties.desc || photoModalData.properties.desc_heb || editingDesc) && (
                editingDesc ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                    <TextField size="small" fullWidth multiline maxRows={3} value={descDraft} onChange={e => setDescDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') setEditingDesc(false); }}
                      autoFocus variant="standard" sx={{ '& .MuiInput-input': { color: 'rgba(255,255,255,0.85)' }, '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255,255,255,0.3)' } }} />
                    <IconButton size="small" onClick={saveDesc}><CheckIcon fontSize="small" sx={{ color: '#66ff66' }} /></IconButton>
                    <IconButton size="small" onClick={() => setEditingDesc(false)}><CloseIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.8)' }} /></IconButton>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Typography variant={isPhone ? 'caption' : 'body2'} sx={{ color: 'rgba(255,255,255,0.75)', ...(isPhone && { fontSize: 11, lineHeight: 1.25 }) }}>{(lang === 'he' && photoModalData.properties.desc_heb) ? photoModalData.properties.desc_heb : photoModalData.properties.desc}</Typography>
                    {isAdmin(authData) && <IconButton size="small" onClick={startEditDesc}><EditIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} /></IconButton>}
                  </div>
                )
              )}
              {!photoModalData.properties.desc && !photoModalData.properties.desc_heb && !editingDesc && isAdmin(authData) && (
                <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.7)' } }} onClick={startEditDesc}>
                  + Add description
                </Typography>
              )}

              {/* Year */}
              {formatYear(photoModalData.properties) && (
                <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.65)' }}>{formatYear(photoModalData.properties)}</Typography>
              )}

              {/* Creator. Hidden on the single-photographer sites (Scholten,
                  Aleksandrowicz), where the header already names them — but shown
                  on the unified site and on collections like TAYCM that hold work
                  by many different photographers.
                  Falls back across languages: TAYCM has creator_heb but no
                  English creator, so requiring `creator` would hide the name. */}
              {(!COLLECTION || isTaycm) &&
                (photoModalData.properties.creator || photoModalData.properties.creator_heb) && (
                <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  {(lang === 'he'
                    ? photoModalData.properties.creator_heb || photoModalData.properties.creator
                    : photoModalData.properties.creator || photoModalData.properties.creator_heb)}
                </Typography>
              )}

              {!hasLocation && <Typography variant={isPhone ? 'caption' : 'body2'} sx={{ color: '#ff8a80', fontStyle: 'italic', ...(isPhone && { fontSize: 10, lineHeight: 1.2 }) }}>{t('itemAwaitsGeotagging', lang)}</Typography>}

              {/* Source link - collection-aware */}
              {isAleksandrowicz ? (
                <Typography variant='caption' sx={{ color: accentText }}>
                  {t('copyrightAleksandrowicz', lang)}
                </Typography>
              ) : isMatson ? (
                matsonPk && <Link variant='caption' target='_blank' rel="noopener" sx={{ color: accentText }}
                  href={`https://www.loc.gov/pictures/collection/matpc/item/${matsonPk}/`}>
                  {t('viewSourceLoc', lang)}
                </Link>
              ) : isBenShahar ? (
                /* Per-item deep link if we have one (the archive URL from metadata),
                   otherwise fall back to the search-page link. */
                photoModalData.properties.href ? (
                  <Link variant='caption' target='_blank' rel="noopener" sx={{ color: accentText }}
                    href={photoModalData.properties.href}>
                    {t('viewSourceTaArchiveDirect', lang)}
                  </Link>
                ) : (
                  <Link variant='caption' target='_blank' rel="noopener" sx={{ color: accentText }}
                    href="https://infocenters.co.il/ta_orkoli/search.asp?lang=HEB&dlang=HEB&module=search&page=criteria&rsvr=10@10&param=%3Cuppernav%3Eglobal%3C/%3E&param2=%3Cnvr%3E1%3C/%3E%3Csearch_type%3Eglobal%3C/%3E%3Cnob%3E0%3C/%3E&site=ta_orkoli">
                    {t('viewSourceTaArchive', lang)}
                  </Link>
                )
              ) : isVanDePoll ? (
                /* Nationaal Archief per-item URL is stored in `properties.href` at
                   import time; falls back to constructing it from additional_id
                   (the dc_identifier / handle UUID) if href is somehow absent. */
                <Link variant='caption' target='_blank' rel="noopener" sx={{ color: accentText }}
                  href={photoModalData.properties.href
                    || (photoModalData.properties.additional_id
                        && `https://www.nationaalarchief.nl/en/research/photo-collection/${photoModalData.properties.additional_id}`)
                    || undefined}>
                  {t('viewSourceNationaalArchief', lang)}
                </Link>
              ) : isTaycm ? (
                /* The museum has no public per-item URLs, so credit the archive
                   rather than linking. Must sit before the Leiden fallback below,
                   which would otherwise emit a dead hdl.handle.net link. */
                <Typography variant='caption' sx={{ color: accentText }}>
                  {t('copyrightTaycm', lang)}
                </Typography>
              ) : (
                photoModalData.properties.external_id && <Link variant='caption' target='_blank' rel="noopener" sx={{ color: accentText }} href={`http://hdl.handle.net/1887.1/item:${photoModalData.properties.external_id}`}>{t('viewSourceLeiden', lang)}</Link>
              )}

              {/* Catalogue number only. external_id is a NAS path: the folder
                  prefix is required for Cantaloupe to resolve the file (delegates.rb
                  branches on the "/"), but means nothing to a reader — so show just
                  the filename, with any extension removed:
                    TAYCM/TAYCM.03.02.02.4  ->  TAYCM.03.02.02.4 */}
              {(isAleksandrowicz || isTaycm) && photoModalData.properties.external_id &&
                <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  {t('itemId', lang, {
                    id: String(photoModalData.properties.external_id)
                          .split('/').pop()
                          ?.replace(/\.(jpe?g|png|tiff?)$/i, '')
                  })}
                </Typography>
              }
              {photoModalData.properties.first_geotagged_by && <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.65)' }}>{t('firstGeotaggedBy', lang, { username: photoModalData.properties.first_geotagged_by })}</Typography>}
              {photoModalData.properties.geotagged_by && photoModalData.properties.geotagged_by !== photoModalData.properties.first_geotagged_by && <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.65)' }}>{t('lastGeotaggedBy', lang, { username: photoModalData.properties.geotagged_by })}</Typography>}
            </Stack>
            </div>
          </div>
        </div>

        {/* Comments pane:
            Desktop — right-side sibling, fixed 300px / 85vh.
            Phone   — stacked below the image, full-width, auto height. */}
        <div style={{
          width: isPhone ? '100%' : 300,
          flexShrink: 0,
          height: isPhone ? 'auto' : '85vh',
          maxHeight: isPhone ? '30vh' : '85vh',
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          backgroundColor: '#fafafa',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <CommentsPanel
            authData={authData}
            itemComments={itemComments}
            handleComment={handleComment}
            handleComments={handleComments}
            itemPk={itemPk}
            lang={lang}
            isPhone={isPhone}
          />
        </div>
      </Dialog>
    ) : null}
  </>
}

export default ItemModal
